import { Link } from 'react-router-dom';
import { ChevronRight, LogOut, Menu, MessageSquareText, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button, buttonClasses } from '@/components/ui';

interface HeaderProps {
  /** Sidans namn. Kommer från rutten i Shell så att varje sida slipper egen rubrikrad. */
  title: string;
  /** Mobilmenyn — knappen syns bara under lg, där spalten är utfälld. */
  menyOppen: boolean;
  onMeny: () => void;
}

/**
 * Topbaren bär sidans identitet: brödsmula och titel till vänster, konto och
 * åtgärder till höger. Logotypen sitter numera i sidomenyn — i 1.0 låg den här
 * och tvingade ner både meny och innehåll ett steg.
 */
export function Header({ title, menyOppen, onMeny }: HeaderProps) {
  const { user, signOut } = useAuth();

  const email = user?.email ?? '';

  return (
    <header className="h-[62px] shrink-0 bg-surface border-b border-ink-200 flex items-center gap-3 sm:gap-4 px-4 sm:px-6">
      <button
        type="button"
        onClick={onMeny}
        aria-label={menyOppen ? 'Stäng menyn' : 'Öppna menyn'}
        aria-expanded={menyOppen}
        aria-controls="primar-meny"
        className="lg:hidden w-9 h-9 shrink-0 grid place-items-center rounded-[9px] bg-nav text-nav-strong"
      >
        {menyOppen ? <X size={18} strokeWidth={2} /> : <Menu size={18} strokeWidth={2} />}
      </button>
      <div className="min-w-0">
        <div className="flex items-center gap-1 text-[11px] text-ink-500 leading-none mb-[3px]">
          <span>El-kretsen</span>
          <ChevronRight size={11} strokeWidth={2} />
          <span className="text-ink-700">{title}</span>
        </div>
        <h1 className="text-[18.5px] font-semibold tracking-[-0.015em] text-ink-900 leading-tight truncate">
          {title}
        </h1>
      </div>

      <div className="ml-auto flex items-center gap-2">
        {email && (
          <span className="hidden md:block text-[11.5px] text-ink-500 mr-1 truncate max-w-[240px]">
            {email}
          </span>
        )}
        {/* Genvägen ligger kvar — den var det folk hittade — men leder nu
            till fliken i stället för att öppna en modal. Modalen tog emot
            och visade aldrig något tillbaka; fliken visar historiken och
            våra svar. */}
        {/* aria-label eftersom texten döljs under sm-brytpunkten. Utan den
            är länken bara en ikon för en skärmläsare — och en ikon utan namn
            läses upp som "länk", vilket inte hjälper någon vidare. */}
        <Link
          to="/feedback"
          aria-label="Feedback"
          className={buttonClasses({ variant: 'secondary', size: 'sm' })}
        >
          <MessageSquareText size={14} strokeWidth={2} aria-hidden />
          <span className="hidden sm:inline">Feedback</span>
        </Link>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<LogOut size={14} strokeWidth={2} />}
          onClick={() => signOut()}
        >
          <span className="hidden sm:inline">Logga ut</span>
        </Button>
      </div>

    </header>
  );
}
